import type { Projection, WorkOrder } from "./api";
import { personName } from "./requests";

export const NOTE_MAX = 1000;

export const KINDS = ["setup", "swap", "resize", "remove", "sticker"] as const;
export type OrderKind = (typeof KINDS)[number];
export type OrderStatus = "open" | "done" | "cancelled";

export const KIND_TEXT: Record<OrderKind, string> = {
  setup: "Set up a container",
  swap: "Swap the container",
  resize: "Change the container size",
  remove: "Remove the container",
  sticker: "Replace the sticker",
};

export const STATUS_TEXT: Record<OrderStatus, string> = { open: "Open", done: "Done", cancelled: "Cancelled" };

/** The sizes that each kind needs: the container to pull, and the container to drop (design 8.6). */
export const NEEDS: Record<OrderKind, { pull: boolean; drop: boolean }> = {
  setup: { pull: false, drop: true },
  swap: { pull: true, drop: true },
  resize: { pull: true, drop: true },
  remove: { pull: true, drop: false },
  sticker: { pull: false, drop: false },
};

const gal = (value: number) => `${Number.isInteger(value) ? value : value.toFixed(1)} gal`;

export function orderError(kind: OrderKind, pull: number | null, drop: number | null, note: string): string {
  const needs = NEEDS[kind];
  if (needs.pull && pull === null) return "Choose the container to pull.";
  if (needs.drop && drop === null) return "Choose the size to drop.";
  if (kind === "resize" && pull === drop) return "Choose a new size for the resize.";
  if (note.trim().length > NOTE_MAX) return `The note can have ${NOTE_MAX} characters at most.`;
  return "";
}

export function cancelError(note: string): string {
  if (!note.trim()) return "Give the reason for the cancel.";
  if (note.trim().length > NOTE_MAX) return `The reason can have ${NOTE_MAX} characters at most.`;
  return "";
}

/** One line for the sizes of an order: "Pull 110 gal, drop 180 gal". */
export function orderText(order: Pick<WorkOrder, "from_size" | "to_size">): string {
  const parts = [
    order.from_size !== null ? `pull ${gal(order.from_size)}` : "",
    order.to_size !== null ? `drop ${gal(order.to_size)}` : "",
  ].filter(Boolean);
  const text = parts.join(", ");
  return text ? text[0].toUpperCase() + text.slice(1) : "";
}

/** Design 8.6: one size, a pair of sizes, or no recommendation. */
export function recommendedText(
  projection: Pick<Projection, "need_gal" | "recommended_sizes" | "recommended_gal"> | null | undefined,
): string {
  if (!projection || projection.need_gal == null) return "No estimate";
  const sizes = projection.recommended_sizes ?? [];
  const need = `for ${gal(projection.need_gal)} each cycle`;
  if (sizes.length === 0) return `No standard size is large enough ${need}`;
  return `${sizes.map(gal).join(" + ")} ${need}`;
}

/** The sub line of the account list: the recommended size, marked when it differs from the capacity. */
export function recommendedLine(
  projection: Pick<Projection, "need_gal" | "recommended_sizes" | "recommended_gal"> | null | undefined,
  capacity: number,
): { text: string; differs: boolean } | null {
  if (!projection || projection.need_gal == null) return null;
  const sizes = projection.recommended_sizes ?? [];
  if (sizes.length === 0) return { text: "No standard size is large enough", differs: true };
  const differs = projection.recommended_gal != null && projection.recommended_gal !== capacity;
  return { text: `Recommended ${sizes.map(gal).join(" + ")}${differs ? `, now ${gal(capacity)}` : ""}`, differs };
}

/** Bold marks only a large change: twice the capacity or more, or half of it or less. */
export function bigChange(recommended: number, capacity: number): boolean {
  if (capacity <= 0) return true;
  return recommended >= 2 * capacity || recommended <= capacity / 2;
}

/** The Recommended column of the account list. */
export function recommendedCell(
  projection: Pick<Projection, "need_gal" | "recommended_sizes" | "recommended_gal"> | null | undefined,
  capacity: number,
): { text: string; now: string | null; strong: boolean } | null {
  if (!projection || projection.need_gal == null) return null;
  const sizes = projection.recommended_sizes ?? [];
  if (sizes.length === 0) return { text: "None large enough", now: null, strong: true };
  const total = projection.recommended_gal ?? sizes.reduce((sum, size) => sum + size, 0);
  const differs = total !== capacity;
  return {
    text: sizes.map(gal).join(" + "),
    now: differs ? `now ${gal(capacity)}` : null,
    strong: differs && bigChange(total, capacity),
  };
}

/** The hint under the size to drop. */
export function dropHint(
  projection: Pick<Projection, "need_gal" | "recommended_sizes" | "recommended_gal"> | null | undefined,
  planned: boolean,
): string {
  if (!planned) return "No estimate. Add a service plan.";
  if (!projection || projection.need_gal == null) return "No estimate.";
  const sizes = projection.recommended_sizes ?? [];
  if (sizes.length === 0) return "No standard size is large enough.";
  return `Recommended: ${sizes.map(gal).join(" + ")} (Estimated)`;
}

/** Who closed the order, and the reason of a cancel. */
export function closedText(order: Pick<WorkOrder, "status" | "closed_by" | "close_note">): string {
  if (order.status === "open") return "";
  const who = order.closed_by ? ` by ${personName(order.closed_by)}` : "";
  if (order.status === "done") return `Done${who}`;
  return `Cancelled${who}${order.close_note ? `: ${order.close_note}` : ""}`;
}

/** A margin fraction as a percent, with up to 2 decimals: 0.125 gives "12.5". */
export const marginText = (fraction: number) => String(Math.round(fraction * 10000) / 100);

const RESIZE_PARAM = "resize";

/** The account page with the resize work order open at this size (rule R-3). */
export const resizeLink = (base: string, accountId: string, size: number) => `${base}/accounts/${accountId}?${RESIZE_PARAM}=${size}`;

/** The size of a resize link that is a standard size, else null. */
export function resizeTarget(search: string, standard: readonly number[]): number | null {
  const size = Number(new URLSearchParams(search).get(RESIZE_PARAM));
  return standard.includes(size) ? size : null;
}

/** The sizes to pull: the containers on the account first, then the standard sizes. */
export function pullSizes(current: readonly number[], standard: readonly number[]): number[] {
  const own = [...new Set(current)].sort((a, b) => a - b);
  return [...own, ...standard.filter((size) => !own.includes(size))];
}

export const sizesText = (sizes: readonly number[]) => sizes.join(", ");

export type ContainersError = { field: "sizes" | "margin"; text: string };

/** Read the settings form; give the field and its error, or the sizes sorted and the margin as a fraction. */
export function containersChange(sizes: string, margin: string): { sizes: number[]; margin: number } | ContainersError {
  const wrong = (text: string): ContainersError => ({ field: "sizes", text });
  const parts = sizes.split(/[\s,]+/).filter(Boolean);
  if (parts.length === 0) return wrong("Give at least one size.");
  if (parts.length > 10) return wrong("Give 10 sizes at most.");
  if (parts.some((part) => !/^\d+$/.test(part))) return wrong("Give each size in whole gallons.");
  const found = parts.map(Number).sort((a, b) => a - b);
  if (found.some((size) => size < 10 || size > 2000)) return wrong("Each size must be from 10 to 2000 gal.");
  if (new Set(found).size !== found.length) return wrong("Give each size only once.");
  const share = Number(margin.trim().replace(/%$/, ""));
  if (margin.trim() && !Number.isFinite(share)) return { field: "margin", text: "Give the margin as a number." };
  if (!margin.trim() || share < 0 || share > 100) {
    return { field: "margin", text: "The margin must be from 0 to 100%." };
  }
  return { sizes: found, margin: Math.round(share * 100) / 10000 };
}
